import React from "react";
import { useLocation } from "react-router";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import { graphql } from "relay-runtime";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import { useClearSearchParam } from "~/search/filters/Filter";
import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { FilterEmptySearchResult } from "./Filters";
import { useFilterRecordingsByCompany } from "./hooks/useFilterRecordingsByCompany";

import type { CompanyFilterCompaniesQuery } from "~/__generated__/CompanyFilterCompaniesQuery.graphql";

import styles from "./AttendeeFilter.module.css";
import filtersStyles from "./Filters.module.css";

const companiesQuery = graphql`
	query CompanyFilterCompaniesQuery($query: String) {
		recordingsCompanies(search: $query)
	}
`;

export const PARAM_NAME = "company";

export default function CompanyFilter() {
	const [inputValue, setInputValue] = React.useState("");
	const [query, setQuery] = React.useState<string>();

	const location = useLocation();
	const selectedCompany = React.useMemo<string | null>(() => {
		const search = new URLSearchParams(location.search);
		const selectedCompany = search.get(PARAM_NAME);
		return selectedCompany || null;
	}, [location.search]);

	const debouncedSearchQuery = React.useMemo(() => {
		return debounce(value => {
			setQuery(value);
		}, 350);
	}, []);

	const setValue = React.useCallback(
		newValue => {
			setInputValue(newValue);
			debouncedSearchQuery(newValue);
		},
		[debouncedSearchQuery],
	);

	const inputRef = React.useRef<HTMLInputElement>(null);

	const onInputFocus = React.useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const onClear = useClearSearchParam(PARAM_NAME, [PARAM_NAME]);

	return (
		<Menu.Sub>
			<Menu.SubTrigger>Company</Menu.SubTrigger>
			<Menu.SubContent onFocus={onInputFocus}>
				<div className={styles.search}>
					<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
					<TextField
						value={inputValue}
						setValue={setValue}
						ref={inputRef}
						placeholder="Search companies"
						autoFocus
						className={styles.searchInput}
					/>
				</div>
				<Menu.Separator />
				<React.Suspense fallback={<span className={styles.loadingState}>Loading…</span>}>
					<CompaniesRenderer query={query} selectedCompany={selectedCompany} />
				</React.Suspense>
				{selectedCompany && (
					<>
						<Menu.Separator />
						<Menu.Item onSelect={onClear} className={filtersStyles.clearFilterOption}>
							Clear filter
						</Menu.Item>
					</>
				)}
			</Menu.SubContent>
		</Menu.Sub>
	);
}

function CompanyOption({ company }: { company: string }) {
	const onCompanyClicked = useFilterRecordingsByCompany(company);

	return (
		<Menu.RadioItem onSelect={onCompanyClicked} value={company}>
			{company}
		</Menu.RadioItem>
	);
}

interface CompaniesRendererProps {
	selectedCompany: string | null;
	query?: string;
}

function CompaniesRenderer({ selectedCompany, query }: CompaniesRendererProps) {
	const { recordingsCompanies } = useLazyLoadQuery<CompanyFilterCompaniesQuery>(
		companiesQuery,
		{ query },
		{ fetchPolicy: "store-or-network" },
	);

	return (
		<Menu.RadioGroup value={selectedCompany || undefined}>
			{recordingsCompanies.length > 0 ? (
				recordingsCompanies.map(company => <CompanyOption key={company} company={company} />)
			) : (
				<FilterEmptySearchResult objectName="companies" />
			)}
		</Menu.RadioGroup>
	);
}
